import { UNCONFIGURED_ORGANIZATION, type OrganizationProfile } from '@chukua/common';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getOrganization } from '../organization.api';

const OrganizationContext = createContext<OrganizationProfile | null>(null);

/**
 * Loads the company this deployment serves, once, before anything renders, so
 * the login page never flashes Chukua's branding before the tenant's. A failed
 * load falls back to Chukua's own branding rather than blocking the app.
 */
export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);

  useEffect(() => {
    getOrganization()
      .catch(() => UNCONFIGURED_ORGANIZATION)
      .then(setProfile);
  }, []);

  useEffect(() => {
    if (!profile) return;
    document.title = profile.name;
    document.documentElement.lang = profile.language;
  }, [profile]);

  if (!profile) return null;
  return <OrganizationContext.Provider value={profile}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationProfile {
  const profile = useContext(OrganizationContext);
  if (!profile) throw new Error('useOrganization must be used inside <OrganizationProvider>');
  return profile;
}
